// Alpine component behind the DatePicker templ component.
//
// Registered by CALLING registerDatePicker(Alpine) from scripts.ts rather than
// by side-effect import: ES imports are hoisted, so a side-effect import would
// evaluate this module before scripts.ts assigns window.Alpine. This mirrors
// registerFeedbackBubble.
//
// All date arithmetic lives in date_picker_logic.ts so it can be unit-tested
// without Alpine or a DOM.

import {
  applyPick,
  buildCalendarDays,
  canGoNextMonth,
  canGoPrevMonth,
  countDays,
  countNights,
  fromISO,
  inBounds,
  shouldPlaceAbove,
  toISO,
  type Bounds,
  type DayCell,
  type PickerMode,
  type PickStep,
} from "./date_picker_logic";

export interface DatePickerConfig {
  mode: PickerMode;
  startISO: string;
  endISO: string;
  minISO: string;
  maxISO: string;
  dateFormat: string;
  placeholder: string;
  endPlaceholder: string;
  countNights: boolean;
  openAbove: boolean;
  submitURL: string;
  htmxTarget: string;
  htmxSwap: string;
  selectRangeLabel: string;
  selectDateLabel: string;
  rangePrefix: string;
  rangeDaysLabel: string;
  nightLabel: string;
  nightsLabel: string;
  requiredLabel: string;
  pickStartLabel: string;
  pickEndLabel: string;
  fromLabel: string;
  untilLabel: string;
  indefiniteLabel: string;
}

interface Rect {
  top: number;
  left: number;
}

export function registerDatePicker(alpine: typeof window.Alpine): void {
  alpine.data("datePicker", (cfg: DatePickerConfig) => ({
    cfg,
    startISO: cfg.startISO,
    endISO: cfg.endISO,
    startDisplay: "",
    endDisplay: "",
    activePicker: null as PickStep | null,
    viewYear: 0,
    viewMonth: 0,
    monthLabel: "",
    days: [] as DayCell[],
    pickerRect: { top: 0, left: 0 } as Rect,
    /**
     * True while the panel sits above its trigger. Starts as the forced
     * cfg.openAbove and is re-measured on every open from the trigger's
     * viewport rect, so a picker near the bottom of the window flips up.
     */
    placeAbove: cfg.openAbove,
    /** Detaches the pending scroll-close listener; null when none is attached. */
    scrollCleanup: null as (() => void) | null,
    /** The trigger the open panel was launched from, so Escape can restore focus. */
    lastTrigger: null as HTMLElement | null,
    /** Values as they were when the panel opened; Escape rolls back to these. */
    snapshot: null as { startISO: string; endISO: string } | null,
    /** Set when the browser rejected the form because a required half is empty. */
    missingStart: false,
    missingEnd: false,
    weekdays: window.__display.names.weekdaysNarrow
      .slice(1)
      .concat(window.__display.names.weekdaysNarrow[0]),

    get bounds(): Bounds {
      return { minISO: this.cfg.minISO, maxISO: this.cfg.maxISO };
    },
    get isRangeSelected(): boolean {
      return !!(this.startISO && this.endISO);
    },
    /** True once the mode's selection is complete — one date or both ends. */
    get hasSelection(): boolean {
      return this.cfg.mode === "singleDay"
        ? !!this.startISO
        : this.isRangeSelected;
    },
    get dayCount(): number {
      return countDays(this.startISO, this.endISO);
    },
    get nightsCount(): number {
      return countNights(this.startISO, this.endISO);
    },
    /** What the trigger badge shows — nights for check-in/check-out ranges. */
    get badgeCount(): number {
      return this.cfg.countNights ? this.nightsCount : this.dayCount;
    },
    get badgeUnitLabel(): string {
      if (!this.cfg.countNights) return this.cfg.rangeDaysLabel;
      return this.nightsCount === 1
        ? this.cfg.nightLabel
        : this.cfg.nightsLabel;
    },
    get canPrev(): boolean {
      return canGoPrevMonth(this.viewYear, this.viewMonth, this.bounds);
    },
    get canNext(): boolean {
      return canGoNextMonth(this.viewYear, this.viewMonth, this.bounds);
    },

    init() {
      // Deliberately does NOT clamp startISO/endISO to the bounds: a stored
      // past value (e.g. a rate plan's 2024 valid_from) must survive untouched
      // and submit unchanged if the operator never edits it.
      this.viewFrom(this.startISO);
      this.syncDisplays();
      this.buildCalendar();
    },

    viewFrom(iso: string) {
      const base = iso ? fromISO(iso) : new Date();
      this.viewYear = base.getFullYear();
      this.viewMonth = base.getMonth();
    },

    openPanel(target: PickStep, triggerEl: HTMLElement | null) {
      if (this.activePicker === target) {
        this.closeAndSubmit();
        return;
      }
      // Snapshot on the transition from closed, never on a half switch, so
      // Escape rolls back to the values the operator saw before opening.
      if (this.activePicker === null) {
        this.snapshot = { startISO: this.startISO, endISO: this.endISO };
      }
      this.activePicker = target;
      // Replace, never add: a listener left behind by an earlier open would pin
      // this component until the container next scrolls. This sits OUTSIDE the
      // triggerEl branch on purpose — reopening on the other half without a
      // trigger element still has to drop the previous half's listener, or a
      // later scroll collapses a panel that is now showing a different half.
      this.detachScrollClose();
      if (triggerEl) {
        this.lastTrigger = triggerEl;
        const r = triggerEl.getBoundingClientRect();
        // openAbove is a force flip; otherwise flip only when the space below
        // is too tight for the panel and the space above is larger.
        this.placeAbove =
          this.cfg.openAbove ||
          shouldPlaceAbove(r.top, r.bottom, window.innerHeight);
        this.pickerRect = this.placeAbove
          ? { top: r.top - 4, left: r.left }
          : { top: r.bottom + 4, left: r.left };
        const scroller = triggerEl.closest("[data-scroll-close]");
        if (scroller) {
          const onScroll = () => this.closePanel();
          scroller.addEventListener("scroll", onScroll, { once: true });
          this.scrollCleanup = () =>
            scroller.removeEventListener("scroll", onScroll);
        }
      }
      // Open on the month of whichever bound is being edited, so a stored past
      // date is visible rather than silently off-screen.
      this.viewFrom(
        target === "end" && this.endISO ? this.endISO : this.startISO,
      );
      this.buildCalendar();
    },

    togglePicker(triggerEl: HTMLElement | null) {
      if (this.activePicker !== null) {
        this.closeAndSubmit();
        return;
      }
      this.openPanel("start", triggerEl);
    },

    closeAndSubmit() {
      if (this.activePicker === null) return;
      this.closePanel();
      this.submitSelection();
    },

    /**
     * Escape cancels: it rolls the values back to the snapshot taken when the
     * panel opened, closes without submitting, and returns focus to the
     * trigger. The native <input type="date"> this component replaced closed
     * on Escape, and a keyboard user who opened the calendar would otherwise
     * be stranded on a dismissed dialog with no way back into the form.
     *
     * Rolling back is what separates Escape from @click.outside. pick() has
     * already written to the form input via x-model, so merely closing would
     * strand a half-edited range: submitSelection() bails when only one end is
     * set, leaving the pill reading "from X → indefinite" while the list still
     * shows the range that was actually applied. Escape must therefore undo,
     * not just dismiss — which is also what the key conventionally means.
     */
    closeViaEscape() {
      if (this.activePicker === null) return;
      const trigger = this.lastTrigger;
      const snap = this.snapshot;
      this.closePanel();
      if (snap) {
        this.startISO = snap.startISO;
        this.endISO = snap.endISO;
        this.syncDisplays();
        this.buildCalendar();
      }
      trigger?.focus();
    },

    /** Every close path goes through here so the scroll listener never leaks. */
    closePanel() {
      this.activePicker = null;
      this.snapshot = null;
      this.detachScrollClose();
    },

    detachScrollClose() {
      if (this.scrollCleanup) {
        this.scrollCleanup();
        this.scrollCleanup = null;
      }
    },

    /** Auto-submit for filter bars: fires only once the mode's selection is complete. */
    submitSelection() {
      if (!this.cfg.submitURL || !window.htmx || !this.hasSelection) return;
      const query =
        this.cfg.mode === "singleDay"
          ? `from=${this.startISO}`
          : `from=${this.startISO}&to=${this.endISO}`;
      const sep = this.cfg.submitURL.includes("?") ? "&" : "?";
      window.htmx.ajax("GET", `${this.cfg.submitURL}${sep}${query}`, {
        target: this.cfg.htmxTarget,
        swap: this.cfg.htmxSwap,
      });
    },

    /**
     * Handles the `invalid` event of a required half's value input. The native
     * bubble is suppressed (the input is invisible) and replaced by the half's
     * error state; focus goes to the first empty half's trigger.
     */
    flagMissing(step: PickStep, triggerEl: HTMLElement | null) {
      if (step === "start") {
        this.missingStart = true;
        triggerEl?.focus();
        return;
      }
      this.missingEnd = true;
      if (!this.missingStart) triggerEl?.focus();
    },

    prevMonth() {
      if (!this.canPrev) return;
      if (this.viewMonth === 0) {
        this.viewMonth = 11;
        this.viewYear -= 1;
      } else {
        this.viewMonth -= 1;
      }
      this.buildCalendar();
    },

    nextMonth() {
      if (!this.canNext) return;
      if (this.viewMonth === 11) {
        this.viewMonth = 0;
        this.viewYear += 1;
      } else {
        this.viewMonth += 1;
      }
      this.buildCalendar();
    },

    pick(iso: string) {
      const result = applyPick(
        {
          startISO: this.startISO,
          endISO: this.endISO,
          step: this.activePicker ?? "start",
        },
        iso,
        this.cfg.mode,
        this.bounds,
      );
      if (!result.changed) return;
      this.startISO = result.startISO;
      this.endISO = result.endISO;
      if (result.close) {
        this.closePanel();
      } else {
        this.activePicker = result.step;
      }
      this.syncDisplays();
      this.buildCalendar();
      // A single-day pick, or the end pick of a range, completes the selection,
      // so it is the close event — @click.outside will find the panel already
      // closed.
      if (result.close) this.submitSelection();
    },

    /**
     * Blanks one half of an optional Range without disturbing the other, so a
     * mis-picked start can be corrected without losing an already-picked end.
     * Neither closes the panel: clearRange() is the one that ends the
     * interaction, because an empty range is a complete (unfiltered) selection
     * while a half-empty one is not.
     */
    clearStart() {
      this.startISO = "";
      // An open panel's Escape snapshot still holds the cleared date; roll it
      // forward, or Escape after Clear brings the old start date back.
      if (this.snapshot) this.snapshot = { ...this.snapshot, startISO: "" };
      // Send the open panel back to step 1. Without this the panel keeps the
      // "end" step it was on, and every later click only rewrites endISO — the
      // operator cannot set a start again without clicking the left half.
      if (this.activePicker !== null) this.activePicker = "start";
      this.syncDisplays();
      this.buildCalendar();
    },

    clearEnd() {
      this.endISO = "";
      // Same roll-forward as clearStart: Escape must not resurrect the end.
      if (this.snapshot) this.snapshot = { ...this.snapshot, endISO: "" };
      this.syncDisplays();
      this.buildCalendar();
    },

    /**
     * Drives the "open-ended" checkbox (OpenEndedLabel). The box mirrors
     * `!endISO`, so it keeps no state of its own: ticking clears the end and
     * ends the interaction; unticking opens the end half, so the end date that
     * replaces "open-ended" is picked right away.
     */
    setOpenEnded(openEnded: boolean, triggerEl: HTMLElement | null) {
      if (openEnded) {
        this.clearEnd();
        this.closePanel();
        return;
      }
      this.openPanel("end", triggerEl);
    },

    clearRange() {
      this.startISO = "";
      this.endISO = "";
      this.closePanel();
      this.syncDisplays();
      this.buildCalendar();
      if (this.cfg.submitURL && window.htmx) {
        window.htmx.ajax("GET", this.cfg.submitURL, {
          target: this.cfg.htmxTarget,
          swap: this.cfg.htmxSwap,
        });
      }
    },

    syncDisplays() {
      this.startDisplay = this.toDisplay(this.startISO);
      this.endDisplay = this.toDisplay(this.endISO);
      if (this.startISO) this.missingStart = false;
      if (this.endISO) this.missingEnd = false;
      // Contract consumed by stop_sale, daily_rate, daily_rate_bulk_modal and
      // blocking. Name and detail shape must not change.
      window.dispatchEvent(
        new CustomEvent("date-picker-synced", {
          detail: { from: this.startISO, to: this.endISO },
        }),
      );
    },

    buildCalendar() {
      this.monthLabel = window.__display.monthYearLong(
        this.viewYear,
        this.viewMonth,
      );
      this.days = buildCalendarDays(this.viewYear, this.viewMonth, this.bounds);
    },

    toDisplay(iso: string): string {
      return iso ? window.__display.formatDate(iso, this.cfg.dateFormat) : "";
    },

    showRangeHighlight(iso: string): boolean {
      return !!(
        this.cfg.mode === "range" &&
        this.startISO &&
        this.endISO &&
        this.startISO !== this.endISO &&
        iso >= this.startISO &&
        iso <= this.endISO
      );
    },

    rangeSpanClass(iso: string): string {
      if (
        iso === this.startISO &&
        this.endISO &&
        this.startISO !== this.endISO
      ) {
        return "left-0 right-0 rounded-l-full";
      }
      if (
        iso === this.endISO &&
        this.startISO &&
        this.startISO !== this.endISO
      ) {
        return "left-0 right-0 rounded-r-full";
      }
      return "left-0 right-0";
    },

    dayTextClass(day: DayCell): string {
      // A stored value outside the bounds sits on a grey circle (see the
      // panel's isLockedSelection binding); its digit must contrast with that
      // circle while the cursor still says it cannot be picked.
      if (this.isLockedSelection(day.iso)) {
        return "text-gray-700 dark:text-gray-100 cursor-not-allowed";
      }
      // Otherwise disabled wins over every other state so an out-of-range day
      // never looks clickable.
      if (day.disabled)
        return "text-gray-300 dark:text-gray-700 cursor-not-allowed";
      if (!day.currentMonth) return "text-gray-300 dark:text-gray-600";
      if (day.iso === this.startISO || day.iso === this.endISO)
        return "text-white";
      if (
        this.startISO &&
        this.endISO &&
        day.iso > this.startISO &&
        day.iso < this.endISO
      ) {
        return "text-brand-600 dark:text-brand-300 hover:bg-gray-100 dark:hover:bg-gray-700";
      }
      return "text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700";
    },

    /** True when the stored value sits outside bounds — selected but locked. */
    isLockedSelection(iso: string): boolean {
      return (
        (iso === this.startISO || iso === this.endISO) &&
        !!iso &&
        !inBounds(iso, this.bounds)
      );
    },

    isoOf(date: Date): string {
      return toISO(date);
    },
  }));
}
